import React, { useState } from 'react'
import {
  getRequiredAttendeesForReservation,
  buildReserveTimeSlotPayload,
  getHandoffWithReservationPayload,
  getMeetingTypeOverride,
  getRoutingIdFromMeetingAttempt,
  SelectTimeSlotParams,
  useAvailabilityOptionsFromMeetingAttempt,
  useSlots,
  useUnifiedRouting,
  useReservationSlot,
  useCancelReservationSlot,
  getMeetingRequestFeature,
  getMeetingRequestFeatureWithGuestFormData,
  GuestFormValues,
  convertToResolvedAssignees,
  getLinkProductFeature,
  isFromDistributionAssignee,
  getBookerInvitationSettings,
} from '@chilipiper/booking-flow'
import { Flex, Text } from '@chilipiper/design-system'
import {
  GuestFormField,
  MeetingInvite,
  PublicMeetingType,
  GuestFormQuestion,
} from '@chilipiper/api-client/src/meeting-types'
import * as v from 'valibot'
import {
  MeetingAttemptResponseV2,
  MeetingAttemptResponseV3,
  MeetingReservation,
  ProductFeatureV2,
} from '@chilipiper/api-client/src/user-availability'
import invariant from 'tiny-invariant'
import { getClientTimezone } from '@chilipiper/date-time'
import { partition } from 'remeda'
import {
  OneClickInitializationRequest,
  MapDataFieldRefString,
  ScheduleMeetingRequestV2,
  ScheduleMeetingResponseV2,
} from '@chilipiper/api-client/src/team-scheduling'
import { BookMeetingBlock, SuggestedTimesBlock } from '../../../chat-data/types'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import { useMeetingAttempt } from '../../../api-queries/userAvailability'
import {
  useBookMeeting,
  useHandoffBooking,
  useInitializeOneClick,
} from '../../../api-queries/meetingsPlatform'
import {
  useMeetingAttemptLinkResource,
  useMeetingAttemptLinkMeetingLimit,
} from '../../../api-queries/team-scheduling'
import { useDataFields } from '../../../api-queries/chiliCrm'
import { TranslationProvider } from '../../../context/TranslationContext'
import { useSuggestedTimesMeetingTypeResolution } from '../../../api-queries/useMeetingTypeResolution'
import { chatPublicEventAction, chiliChatEventName } from '../../../constants'
import { useGetGuestState } from '../../../api-queries/chat-platform-chat'
import { BookingFlowBlock } from './BookingFlowBlock'
import { StyledBookMeetingContainer } from './styles'
import {
  mapMeetingAttendees,
  mapQuestionDataTypeToDataFieldDataType,
  ErrorSchema,
  getMeetingTypeId,
  getFieldText,
  transformMeetingLocation,
  updateGuestFormData,
} from './helpers'
import { BookingFlowBlockLoading } from './BookingFlowBlockLoading'

interface Props {
  block: SuggestedTimesBlock
}

const useMeetingAttemptData = (tenantId: string, attemptId: string) => {
  const { data: meetingAttempt } = useMeetingAttempt({
    tenantId,
    meetingAttemptId: attemptId,
  })

  const routingId = getRoutingIdFromMeetingAttempt(meetingAttempt)

  return {
    meetingAttempt,
    routingId,
  }
}

const useMeetingTypeData = (
  tenantId: string,
  block: SuggestedTimesBlock,
  meetingAttempt?: MeetingAttemptResponseV3
) => {
  const originalMeetingTypeId = getMeetingTypeId(block.payload.meetingTypeId, meetingAttempt)
  return useSuggestedTimesMeetingTypeResolution(tenantId, originalMeetingTypeId)
}

const useGuestFormFields = (
  meetingType: PublicMeetingType | undefined,
  guestFormFilledData: Record<string, string>,
  bookingError: unknown,
  forceShowAllFields: boolean = false
) => {
  const { tenantId } = useMessageFeedContext()

  const getFields = () => {
    const mapField = (field: GuestFormQuestion | GuestFormField) => {
      const id = 'id' in field ? field.id : field.reference
      const initialValue = guestFormFilledData[id]
      const { error, label, description } = getFieldText(field.localizations) ?? {}
      const isRequired =
        field.type === 'Field' ? field.requirement.type === 'Required' : field.isRequired

      const getHiddenValue = (field: GuestFormField | GuestFormQuestion) => {
        if (field.type === 'Field' && field.requirement.type === 'Hidden') {
          return field.requirement.value
        }
        return undefined
      }
      return {
        id,
        label,
        isRequired,
        description,
        initialValue,
        hiddenValue: getHiddenValue(field),
        error: isRequired && !initialValue ? (error ?? 'This value is required') : undefined,
        dataType:
          'id' in field ? mapQuestionDataTypeToDataFieldDataType(field.dataType) : undefined,
      }
    }

    const getAllFields = () => {
      if (!meetingType) {
        return []
      }

      if (meetingType.guestForm.type === 'DataFields') {
        return meetingType.guestForm.fields.map(mapField)
      }

      if (meetingType.guestForm.type === 'Mixed') {
        return meetingType.guestForm.items.map(mapField)
      }

      return []
    }

    const getFormInvalidError = (bookingError: unknown) => {
      const { success, output } = v.safeParse(ErrorSchema, bookingError)
      if (success && output.error.errorCode === 'meetings-api-service.form-validation-failed') {
        return output
      }
      return null
    }

    const allFields = getAllFields() ?? []

    if (meetingType?.guestForm.isAlwaysShowFormEnabled || forceShowAllFields) {
      return allFields
    }

    const hiddenFields = allFields.filter(field => field.hiddenValue !== undefined)
    const formInvalidError = getFormInvalidError(bookingError)

    if (formInvalidError || hiddenFields.length > 0) {
      const filteredFields = allFields.filter(field => {
        return (
          formInvalidError?.error.errorMessage.includes(field.id) ||
          hiddenFields.some(hiddenField => hiddenField.id === field.id)
        )
      })

      return filteredFields
    }

    return hiddenFields
  }

  const [questions, fields] = partition(getFields(), field => !!field.dataType)

  const { data: dataFields } = useDataFields(tenantId, fields?.map(field => field.id) ?? [])
  const formFields = fields
    ?.map(field => {
      const dataField = dataFields?.elements?.find(dataField => dataField.reference === field.id)
      return {
        ...field,
        dataType: field.dataType ?? dataField?.dataType,
      }
    })
    .concat(questions ?? [])

  return { formFields }
}

const useBookingOperations = (tenantId: string, _meetingType?: PublicMeetingType) => {
  const {
    mutate: bookMeetingMutation,
    isPending: isBookingMeetingMutation,
    error: bookingError,
    data: bookingData,
  } = useBookMeeting(tenantId)

  const {
    mutate: initializeOneClick,
    isPending: isInitializingOneClick,
    error: initializationError,
    data: initializationData,
  } = useInitializeOneClick(tenantId)

  const { mutate: handleHandoffScheduleMeeting, isPending: isLoadingHandoffBooking } =
    useHandoffBooking(tenantId)

  return {
    bookMeetingMutation,
    bookingError,
    bookingData,
    isBookingMeetingMutation,
    initializeOneClick,
    isInitializingOneClick,
    initializationError,
    initializationData,
    handleHandoffScheduleMeeting,
    isLoadingHandoffBooking,
  }
}

const useAvailabilityData = (
  tenantId: string,
  meetingAttempt?: MeetingAttemptResponseV3,
  meetingType?: PublicMeetingType
) => {
  const [meetingTypeVersion, setMeetingTypeVersion] = useState(new Date().toISOString())
  const { data: linkResource, refetch: refetchLinkResource } = useMeetingAttemptLinkResource(
    tenantId,
    meetingAttempt
  )
  const availabilityFeature =
    meetingAttempt?.type === 'Available' && meetingAttempt?.attempt.features.highPriorityMeeting
      ? 'highPriorityReservation'
      : undefined

  const { data: linkMeetingLimit } = useMeetingAttemptLinkMeetingLimit({
    tenantId,
    meetingAttempt,
    meetingType,
  })

  const bookerInvitationSettings = getBookerInvitationSettings({
    bookerInvitation: linkResource?.bookerInvitationSettings,
  })

  const routing = useUnifiedRouting({
    tenantId,
    linkResource,
    productFeature:
      meetingAttempt?.type === 'Available' ? meetingAttempt?.attempt.productFeature : undefined,
    meetingAttemptBookerId:
      meetingAttempt?.type === 'Available' ? meetingAttempt?.attempt.bookerId : undefined,
    searchParams: new URLSearchParams(),
  })

  const routingResult =
    routing && routing?.type !== 'HandoffRouter' ? routing.routingResult : undefined
  const meetingTypeOverride = getMeetingTypeOverride(
    meetingAttempt?.type === 'Available' ? meetingAttempt?.attempt : undefined
  )

  const availabilityOptions = useAvailabilityOptionsFromMeetingAttempt({
    linkResource,
    meetingAttempt,
    linkMeetingLimit,
    bookerInvitationSettings,
    routingResult,
  })

  const {
    data: timeSlots,
    isLoading: isSlotsLoading,
    onUpdateTimeSlots,
    refetchSlots,
    hasError,
  } = useSlots({
    meetingTypeVersion,
    availabilityOptions,
    tenantId,
    intervalDuration: '21 days',
    meetingTypeId: meetingType?.id ?? '',
    feature: availabilityFeature,
    meetingTypeOverride: {
      meetingDurationOverride: meetingTypeOverride?.duration,
    },
  })

  if (
    timeSlots?.meetingTypeRef.timestamp &&
    timeSlots?.meetingTypeRef.timestamp !== meetingTypeVersion
  ) {
    setMeetingTypeVersion(timeSlots?.meetingTypeRef.timestamp)
  }

  return {
    timeSlots,
    availabilityOptions,
    linkResource,
    routing,
    isLoading: isSlotsLoading,
    meetingLimit: linkMeetingLimit,
    refetchSlots,
    onUpdateTimeSlots,
    bookerInvitationSettings,
    refetchLinkResource,
    hasError,
    meetingTypeOverride,
    availabilityFeature,
  }
}

const createMeetingInvite = (
  meetingAttempt: MeetingAttemptResponseV2,
  meetingType: PublicMeetingType
): MeetingInvite | null => {
  if (meetingAttempt.type !== 'Available' && meetingAttempt.type !== 'Booked') {
    return null
  }

  return {
    title: meetingAttempt.attempt.meetingInvite.titleOverride ?? meetingType.meetingInvite.title,
    description:
      meetingAttempt.attempt.meetingInvite.descriptionOverride ??
      meetingType.meetingInvite.description,
    duration:
      meetingAttempt.attempt.meetingInvite.meetingDurationOverride ??
      meetingType.meetingInvite.duration,
    locations:
      meetingAttempt.attempt.meetingInvite.possibleLocationsOverride ??
      meetingType.meetingInvite.locations,
  }
}

const getCrmData = (productFeature: ProductFeatureV2) => {
  if (productFeature.type === 'HandoffRouting') {
    return { crmExplicits: undefined, crmId: undefined }
  }
  return {
    crmExplicits: productFeature.crmExplicits,
    crmId: productFeature.primaryGuestV2?.crmId,
  }
}

const createCommonMeetingData = (
  email: string,
  meetingAttempt: MeetingAttemptResponseV2,
  meetingInvite: MeetingInvite
): Pick<
  ScheduleMeetingRequestV2,
  'primaryGuest' | 'attendees' | 'meetingTypeOverride' | 'crmExplicits'
> | null => {
  if (meetingAttempt.type !== 'Available' && meetingAttempt.type !== 'Booked') {
    return null
  }

  const { crmExplicits, crmId } = getCrmData(meetingAttempt.attempt.productFeature)

  return {
    primaryGuest: {
      type: 'External',
      email,
      timezone: getClientTimezone(),
      crmId,
    },
    attendees: mapMeetingAttendees(meetingAttempt.attempt.additionalAttendees),
    crmExplicits,
    meetingTypeOverride: {
      meetingInvite: {
        description: {
          type: meetingInvite.description ? 'Change' : 'LeaveAsIs',
          value: meetingInvite.description,
        },
        title: {
          type: meetingInvite.title ? 'Change' : 'LeaveAsIs',
          value: meetingInvite.title,
        },
      },
      buffers: {
        before: { type: 'LeaveAsIs' },
        after: { type: 'LeaveAsIs' },
      },
      reminder: {
        reminders: {
          type: 'LeaveAsIs',
        },
      },
    },
  }
}

export const SuggestedTimes = ({ block }: Props) => {
  const [isRescheduling, setIsRescheduling] = useState(false)
  const { tenantId, workspaceId = '', guestId } = useMessageFeedContext()
  const [guestFormFilledData, setGuestFormFilledData] = useState<Record<string, string>>(
    {} as Record<string, string>
  )
  const [optimisticMeetingId, setOptimisticMeetingId] = useState<string>()
  const [existingReservation, setExistingReservation] = useState<MeetingReservation>()
  const { data: guestData } = useGetGuestState(tenantId, guestId)
  const { meetingAttempt, routingId } = useMeetingAttemptData(tenantId, block.payload.attemptId)

  const { meetingType } = useMeetingTypeData(tenantId, block, meetingAttempt)

  const {
    bookMeetingMutation,
    bookingError,
    bookingData,
    isBookingMeetingMutation,
    initializeOneClick,
    isInitializingOneClick,
    initializationData,
    handleHandoffScheduleMeeting,
    isLoadingHandoffBooking,
  } = useBookingOperations(tenantId, meetingType)

  const {
    isLoadingReservation,
    onReserveTimeSlot,
    error: reserveTimeSlotError,
  } = useReservationSlot()
  const { onCancelReservation: cancelMeetingAttemptReservation } = useCancelReservationSlot()

  const {
    timeSlots,
    linkResource,
    availabilityOptions,
    routing,
    isLoading,
    refetchLinkResource,
    refetchSlots,
    bookerInvitationSettings,
    meetingLimit,
  } = useAvailabilityData(tenantId, meetingAttempt, meetingType)

  const alwaysShowForm = meetingType?.guestForm.isAlwaysShowFormEnabled

  const productFeature = getLinkProductFeature(
    meetingAttempt?.type === 'Available' ? meetingAttempt?.attempt.productFeature : undefined
  )

  const guestEmail =
    productFeature?.primaryGuest?.email ??
    (linkResource?.type === 'HandoffRouter' ? linkResource.primaryGuest?.email : undefined)
  const shouldForceShowAllFields =
    initializationData?.type === 'Success' &&
    initializationData.missingRequiredFields &&
    initializationData.missingRequiredFields.length > 0

  const { formFields } = useGuestFormFields(
    meetingType,
    guestFormFilledData,
    bookingError,
    shouldForceShowAllFields
  )

  const meetingInvite =
    meetingAttempt && meetingType ? createMeetingInvite(meetingAttempt, meetingType) : null

  const isFlexibleHandoffRouting =
    linkResource?.type === 'HandoffRouter' &&
    linkResource.selectedAssignees?.some(
      assignee =>
        isFromDistributionAssignee(assignee) && assignee.algorithm === 'AvailabilityOptimised'
    )

  const handleHandoffBookMeeting = ({
    slot,
    availableRoundRobinAssignees,
    reservation,
    timezone,
  }: SelectTimeSlotParams & {
    reservation?: MeetingReservation
  }) => {
    const handoffRoutingId = getRoutingIdFromMeetingAttempt(meetingAttempt)
    const handoffRouting = linkResource?.type === 'HandoffRouter' ? linkResource : undefined
    const selectedAssignees = handoffRouting?.selectedAssignees

    invariant(handoffRoutingId, 'Handoff routing id not found')
    invariant(meetingType, 'Meeting type not found')
    invariant(timeSlots, 'Time slots not found')
    invariant(selectedAssignees, 'Selected assignees not found')

    if (isFlexibleHandoffRouting) {
      return handleHandoffScheduleMeeting({
        routingId: handoffRoutingId,
        params: {
          type: 'ScheduleByDistribution',
          assignees: convertToResolvedAssignees({
            selectedAssignees: selectedAssignees,
            availableRoundRobinAssignees,
          }),
          guestTimezone: timezone,
          timeInterval: {
            startsAt: slot,
            duration: meetingType.meetingInvite.duration,
          },
          meetingTypeRelevantAt: timeSlots.meetingTypeRef.timestamp,
          location: meetingType.meetingInvite.locations.default,
          secondaryFeature: ['SuggestedTimes'],
        },
      })
    }

    invariant(reservation, 'Missing required data for Handoff scheduling meeting with reservation')

    const payload = getHandoffWithReservationPayload({
      reservationId: reservation.id,
      selectedAssignees: selectedAssignees.filter(
        assignee => !isFromDistributionAssignee(assignee)
      ),
      location: meetingType.meetingInvite.locations.default,
      pathname: '/suggested-times',
      timezone: getClientTimezone(),
    })

    handleHandoffScheduleMeeting(
      {
        routingId: handoffRoutingId,
        params: payload,
      },
      {
        onSuccess: response => {
          if (response.type === 'ScheduledByReservation') {
            setOptimisticMeetingId(response.meetingId)
          }
        },
        onError: () => {
          cancelMeetingAttemptReservation({
            tenantId,
            meetingAttemptId: block.payload.attemptId,
            reservationId: reservation.id,
          })
        },
      }
    )
  }

  const handleBookMeeting = async ({
    slot,
    userIds,
    availableRoundRobinAssignees,
    guestFormData,
    onError,
    timezone,
  }: SelectTimeSlotParams & {
    guestFormData: GuestFormValues
    onError: () => void
  }) => {
    if (!meetingAttempt || !meetingType || !meetingInvite || !timeSlots) {
      return onError()
    }

    if (meetingAttempt.type !== 'Available' && meetingAttempt.type !== 'Booked') {
      return onError()
    }

    setGuestFormFilledData(guestFormData as Record<string, string>)
    const guestFormEmail = guestFormData['PersonEmail'] ?? guestFormFilledData['PersonEmail']
    const email = Array.isArray(guestFormEmail) ? guestFormEmail[0] : guestFormEmail
    if (!email) {
      return onError()
    }

    window.dispatchEvent(
      new CustomEvent(chiliChatEventName, {
        detail: {
          action: chatPublicEventAction.TIMESLOT_SELECTED,
          args: {
            time: slot,
            timezone: getClientTimezone(),
            duration: meetingInvite.duration,
          },
        },
      })
    )

    const diff = updateGuestFormData(guestFormData, guestData?.guestData ?? {})
    const hasGuestFormDataChanged = Object.keys(diff).length > 0

    if (hasGuestFormDataChanged) {
      window.dispatchEvent(
        new CustomEvent(chiliChatEventName, {
          detail: { action: chatPublicEventAction.DATA_CAPTURED, args: { formData: diff } },
        })
      )
    }

    let reservation = existingReservation

    if (!reservation && !isFlexibleHandoffRouting) {
      const requiredAttendees = getRequiredAttendeesForReservation({
        linkResource,
        productFeature: meetingAttempt?.attempt.productFeature,
        bookerInvitationSettings,
        ownershipRoutingResult:
          routing?.type === 'OwnershipSchedulingLink' ? routing.routingResult : undefined,
        availableRoundRobinAssignees,
        meetingLimit,
      })

      const payload = buildReserveTimeSlotPayload({
        users: requiredAttendees,
        meetingTypeRef: timeSlots.meetingTypeRef,
        slotStartsAt: slot,
        meetingTypeOverride: getMeetingTypeOverride(meetingAttempt?.attempt),
      })

      const reservationResult = await onReserveTimeSlot({
        tenantId,
        attemptId: meetingAttempt.attempt.id,
        payload,
      })

      if (!reservationResult) {
        return onError()
      }

      reservation = reservationResult
      setExistingReservation(reservationResult)
      window.dispatchEvent(
        new CustomEvent(chiliChatEventName, {
          detail: {
            action: chatPublicEventAction.BOOKED,
            args: { reservationId: reservationResult.id },
          },
        })
      )
    }

    const productFeature = meetingAttempt.attempt.productFeature

    if (productFeature.type === 'HandoffRouting') {
      return handleHandoffBookMeeting({
        slot,
        userIds,
        availableRoundRobinAssignees,
        reservation,
        timezone,
      })
    }

    const feature = getMeetingRequestFeature({
      productFeature,
      reservationId: reservation?.id,
      duration: meetingType?.meetingInvite.duration,
      slotStartAt: slot,
      selectedMeetingTypeId: meetingType?.id,
      meetingAttemptId: meetingAttempt?.attempt.id,
      groupSchedulingLinkHostId:
        linkResource?.type === 'GroupSchedulingLink' ? linkResource.hostId : undefined,
      availableRoundRobinAssignees,
      expectedHost: routing?.type !== 'HandoffRouter' ? routing?.expectedHost : undefined,
      ownershipRoutingResult:
        routing?.type === 'OwnershipSchedulingLink' ? routing.routingResult : undefined,
      roundRobinRoutingResult:
        routing?.type === 'RoundRobinSchedulingLink' ? routing.routingResult : undefined,
    })

    if (!feature) {
      return onError()
    }

    const commonData = createCommonMeetingData(email, meetingAttempt, meetingInvite)

    if (!commonData) {
      return onError()
    }

    const shouldShowForm =
      alwaysShowForm ||
      (initializationData?.type === 'Success' &&
        initializationData.missingRequiredFields &&
        initializationData.missingRequiredFields.length > 0)

    if (shouldShowForm) {
      const featureWithGuestForm = getMeetingRequestFeatureWithGuestFormData(
        feature,
        guestFormData,
        meetingType
      )

      const data: ScheduleMeetingRequestV2 = {
        ...commonData,
        feature: {
          ...featureWithGuestForm,
          uiComponent: 'ChatWidget',
          meetingAttemptId: meetingAttempt.attempt.id,
          secondaryFeature: [],
        },
        location: transformMeetingLocation(meetingType.meetingInvite.locations.default),
      }

      bookMeetingMutation(data, {
        onSuccess: response => {
          if (response.type === 'Accepted') {
            window.dispatchEvent(
              new CustomEvent(chiliChatEventName, {
                detail: {
                  action: chatPublicEventAction.BOOKING_CONFIRMED,
                  args: { meetingId: response.meetingId },
                },
              })
            )
            setOptimisticMeetingId(response.meetingId)
          }
          setExistingReservation(undefined)
        },
        onError: () => {
          if (reservation?.id) {
            cancelMeetingAttemptReservation({
              reservationId: reservation.id,
              tenantId,
              meetingAttemptId: block.payload.attemptId,
            })
          }
          setExistingReservation(undefined)
        },
      })
    } else {
      const initData: OneClickInitializationRequest = {
        ...commonData,
        meetingTypeReference: {
          meetingTypeId: timeSlots?.meetingTypeRef?.id ?? meetingType.id,
          timestamp: timeSlots?.meetingTypeRef?.timestamp ?? new Date().toISOString(),
        },
        feature,
        partialGuestData: {
          values: Object.entries(guestFormData).reduce((acc, [key, v]) => {
            const value = Array.isArray(v) ? v[0] : v
            if (value) {
              acc[key] = value
            }
            return acc
          }, {} as MapDataFieldRefString),
        },
      }

      initializeOneClick(initData, {
        onSuccess: response => {
          if (
            response.type === 'Success' &&
            response.missingRequiredFields &&
            response.missingRequiredFields.length > 0
          ) {
            return
          }

          const featureForRegularBooking = getMeetingRequestFeatureWithGuestFormData(
            feature,
            guestFormData,
            meetingType
          )

          const regularBookingData: ScheduleMeetingRequestV2 = {
            ...commonData,
            feature: {
              ...featureForRegularBooking,
              uiComponent: 'ChatWidget',
              meetingAttemptId: meetingAttempt.attempt.id,
              secondaryFeature: [],
            },
            location: meetingType.meetingInvite.locations.default,
            oneClickSchedulingId:
              response.type === 'Success' ? response.oneClickSchedulingId : undefined,
          }

          bookMeetingMutation(regularBookingData, {
            onSuccess: bookingResponse => {
              if (bookingResponse.type === 'Accepted') {
                window.dispatchEvent(
                  new CustomEvent(chiliChatEventName, {
                    detail: {
                      action: chatPublicEventAction.BOOKING_CONFIRMED,
                      args: { meetingId: bookingResponse.meetingId },
                    },
                  })
                )
                setOptimisticMeetingId(bookingResponse.meetingId)
              }
              setExistingReservation(undefined)
            },
            onError: () => {
              if (existingReservation?.id) {
                cancelMeetingAttemptReservation({
                  reservationId: existingReservation.id,
                  tenantId,
                  meetingAttemptId: block.payload.attemptId,
                })
              }
              setExistingReservation(undefined)
            },
          })
        },
        onError: () => {
          return onError()
        },
      })
    }
  }

  const handleRefreshAvailability = () => {
    if (routingId) {
      void refetchLinkResource()
    }
    void refetchSlots()
  }

  if (!meetingAttempt || !meetingType) {
    return (
      <BookingFlowBlockLoading
        meetingId={(() => {
          if (!meetingAttempt) {
            return undefined
          }
          if (meetingAttempt.type === 'Booked') {
            return meetingAttempt.meetingId
          }
          return ''
        })()}
      />
    )
  }

  if (meetingAttempt.type === 'NotFound') {
    return (
      <StyledBookMeetingContainer>
        <Flex p={7} my={1} justifyContent='center' alignItems='center'>
          <Text color='fg/caution' ml={2} fontWeight={500}>
            Failed to load suggested times
          </Text>
        </Flex>
      </StyledBookMeetingContainer>
    )
  }

  if (!meetingInvite) {
    return null
  }

  const fields = () => {
    if (bookingError) {
      return formFields
    }

    if (
      initializationData?.type === 'Success' &&
      initializationData.missingRequiredFields &&
      initializationData.missingRequiredFields.length > 0
    ) {
      return formFields
    }

    if (initializationData === undefined && !alwaysShowForm) {
      const emailField = formFields.find(field => field.id === 'PersonEmail')
      const hiddenFields = formFields
        .filter(field => field.hiddenValue !== undefined)
        .map(field => ({ ...field, error: undefined }))

      if (!emailField) {
        return hiddenFields
      }
      return [{ ...emailField, error: undefined }, ...hiddenFields]
    }

    return formFields
  }

  const finalFields = fields()

  const suggestedTimes = block.payload.slots.map(slot => slot.start)

  const blockData: Omit<BookMeetingBlock, 'assignees'> = {
    meetingTypeId: meetingType?.id ?? '',
    sent: block.sent,
    id: block.id ?? '',
    type: 'BookMeeting',
    author: block.author,
    roomId: block.roomId,
    statuses: block.statuses,
    timestamp: block.timestamp,
    meetingId: meetingAttempt.type === 'Booked' ? meetingAttempt.meetingId : optimisticMeetingId,
  }

  const isBookingMeeting =
    existingReservation !== undefined &&
    !(
      bookingData?.type === 'NoAvailableAssignees' ||
      bookingData?.type === 'Disabled' ||
      bookingData?.type === 'ReservationFailure'
    ) &&
    bookingError === undefined

  const isNoAvailableAssignee = !(bookingData?.type === 'NoAvailableAssignees')

  const isLoadingBooking =
    isBookingMeetingMutation ||
    isLoadingReservation ||
    isInitializingOneClick ||
    isLoadingHandoffBooking

  const error = mapBookingDataToError(bookingData)

  const initialTimezone =
    meetingType?.forceGuestTimezone.type === 'Enabled'
      ? meetingType.forceGuestTimezone.zoneId
      : undefined

  const hostId = (() => {
    if (!routing) {
      return undefined
    }
    if (routing.type === 'HandoffRouter') {
      const firstUserAssignee = routing.selectedAssignees.find(
        assignee => assignee.type === 'User' || assignee.type === 'UserFromDistribution'
      )
      if (firstUserAssignee?.type === 'User') {
        return firstUserAssignee.id
      }
      if (firstUserAssignee?.type === 'UserFromDistribution') {
        return firstUserAssignee.user
      }
      return
    }
    if (routing.expectedHost.type === 'User') {
      return routing.expectedHost.userId
    }
  })()

  return (
    <TranslationProvider tenantId={tenantId} workspaceId={workspaceId}>
      <BookingFlowBlock
        error={error}
        block={blockData}
        fields={finalFields}
        isLoading={isLoading}
        timeSlots={timeSlots}
        guestEmail={guestEmail}
        meetingInvite={meetingInvite}
        isRescheduling={isRescheduling}
        suggestedTimes={suggestedTimes}
        initialTimezone={initialTimezone}
        onBookMeeting={handleBookMeeting}
        availabilityOptions={availabilityOptions}
        hostId={hostId}
        isBookingMeeting={isLoadingBooking}
        isMeetingReserved={isBookingMeeting}
        onReschedulingChange={setIsRescheduling}
        isNoAvailableAssignee={isNoAvailableAssignee}
        refetchAvailability={handleRefreshAvailability}
        claimMeetingAttemptReservationError={reserveTimeSlotError}
      />
    </TranslationProvider>
  )
}

const mapBookingDataToError = (bookingData?: ScheduleMeetingResponseV2) => {
  if (bookingData?.type === 'NoAvailableAssignees') {
    return 'No Available Assignees'
  }
  if (bookingData?.type === 'Disabled') {
    return 'Disabled'
  }
  if (bookingData?.type === 'ReservationFailure') {
    return 'Reservation Failure'
  }
  if (bookingData?.type === 'ProspectLimitReached') {
    return 'Prospect Limit Reached'
  }
  return undefined
}
